/**
 * Bar-by-bar alignment strips (W2-08 timing; ARCHITECTURE.md §3.2 `core/audio-dsp/alignment.ts`; DESIGN.md §5.2
 * "Bar by bar"; ux-landscape U§2.1, the osu!lazer timing comparison view).
 *
 * One row per bar: the bar's span of music time [start, start + length) is cut into `cols` equal columns and each
 * column holds the largest onset-envelope value inside it (so a sharp onset is never missed between two samples,
 * at any zoom). Rows are laid out one after the other in a single Float32Array (row r, column c at r * cols + c).
 * With the right tempo the onsets of every bar fall into the same columns (straight vertical stripes). A tempo that
 * is slightly too low makes every assumed bar a little longer than the music's, so the onsets arrive earlier in
 * each next row: the stripes slant to the left going down; too high slants them to the right.
 */

import { envelopeMax } from './onset';
import type { OnsetEnvelope } from './types';

/** Largest number of columns per row (the view never needs more than its pixel width). */
export const MAX_ALIGNMENT_COLS = 2048;
/** Largest number of rows computed at once. */
export const MAX_ALIGNMENT_ROWS = 4096;

/**
 * Alignment strips. `barStartsSec[r]` and `barLenSec[r]` are the music time (s) and length (s) of row r; rows with
 * a missing or non-positive length are empty. `cols` is clamped to 1..MAX_ALIGNMENT_COLS.
 */
export function alignmentRows(
  e: OnsetEnvelope,
  barStartsSec: readonly number[],
  barLenSec: readonly number[],
  cols: number,
): Float32Array {
  const c = Math.max(1, Math.min(MAX_ALIGNMENT_COLS, Math.floor(Number.isFinite(cols) ? cols : 1)));
  const rows = Math.min(MAX_ALIGNMENT_ROWS, barStartsSec.length);
  const out = new Float32Array(rows * c);
  for (let r = 0; r < rows; r++) {
    const start = barStartsSec[r];
    const len = barLenSec[r];
    if (start === undefined || len === undefined || !Number.isFinite(start) || !(len > 0)) continue;
    const w = len / c;
    for (let k = 0; k < c; k++) {
      const a = start + k * w;
      out[r * c + k] = envelopeMax(e, a, a + w);
    }
  }
  return out;
}

/** A row weaker than this fraction of the strongest row has no usable stripes. */
const WEAK_ROW = 0.25;

/**
 * Phase of the stripes per row, as a column in 0..cols / beatsPerRow (NaN for rows without clear onsets): the circular
 * mean of the columns at the beat frequency (`beatsPerRow` stripes per row), weighted by squared values so the
 * onsets dominate. With one beat per row this is simply where the onset energy collects.
 */
export function rowCentroids(rows: Float32Array, cols: number, beatsPerRow = 1): Float64Array {
  const c = Math.max(1, Math.floor(cols));
  const b = Math.max(1, Math.round(beatsPerRow));
  const span = c / b;
  const n = Math.floor(rows.length / c);
  const out = new Float64Array(n);
  // Rows whose strongest column is weak next to the strongest row (silence, a fade, the edge of the song) say
  // nothing about the stripes.
  let top = 0;
  for (let i = 0; i < n * c; i++) top = Math.max(top, rows[i] ?? 0);
  for (let r = 0; r < n; r++) {
    let rowMax = 0;
    for (let k = 0; k < c; k++) rowMax = Math.max(rowMax, rows[r * c + k] ?? 0);
    if (!(rowMax >= WEAK_ROW * top) || !(rowMax > 0)) {
      out[r] = Number.NaN;
      continue;
    }
    let sx = 0;
    let sy = 0;
    let total = 0;
    for (let k = 0; k < c; k++) {
      const v = rows[r * c + k] ?? 0;
      const w = v * v;
      const a = (2 * Math.PI * b * (k + 0.5)) / c;
      sx += w * Math.cos(a);
      sy += w * Math.sin(a);
      total += w;
    }
    if (!(total > 0) || Math.hypot(sx, sy) < 1e-9 * total) {
      out[r] = Number.NaN;
      continue;
    }
    const angle = Math.atan2(sy, sx);
    out[r] = ((((angle / (2 * Math.PI)) * span) % span) + span) % span;
  }
  return out;
}

/**
 * Drift of the stripes in columns per bar: the slope of the unwrapped stripe phases (`rowCentroids`) against the
 * row index (least squares; rows without onsets skipped). Positive = drifting right (tempo too high), negative =
 * drifting left (tempo too low); 0 when unknown. Drifts of half a beat or more per bar alias.
 */
export function stripeDrift(rows: Float32Array, cols: number, beatsPerRow = 1): number {
  const c = Math.max(1, Math.floor(cols));
  const span = c / Math.max(1, Math.round(beatsPerRow));
  const cent = rowCentroids(rows, c, beatsPerRow);
  const xs: number[] = [];
  const ys: number[] = [];
  let prev: number | null = null;
  let unwrap = 0;
  for (let r = 0; r < cent.length; r++) {
    const v = cent[r] ?? Number.NaN;
    if (!Number.isFinite(v)) continue;
    if (prev !== null) {
      const d = v - prev;
      if (d > span / 2) unwrap -= span;
      else if (d < -span / 2) unwrap += span;
    }
    prev = v;
    xs.push(r);
    ys.push(v + unwrap);
  }
  const n = xs.length;
  if (n < 2) return 0;
  let mx = 0;
  let my = 0;
  for (let i = 0; i < n; i++) {
    mx += xs[i] ?? 0;
    my += ys[i] ?? 0;
  }
  mx /= n;
  my /= n;
  let sxx = 0;
  let sxy = 0;
  for (let i = 0; i < n; i++) {
    const dx = (xs[i] ?? 0) - mx;
    sxx += dx * dx;
    sxy += dx * ((ys[i] ?? 0) - my);
  }
  return sxx > 0 ? sxy / sxx : 0;
}
